import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Sparkles, Volume2, VolumeX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { sendVipChatMessage, speakKonshusVoice } from "@/lib/api";
import { pickText } from "@/lib/content";
import { KONSHUS_NAME, KONSHUS_VIDEO_URL, KONSHUS_POSTER_URL } from "@/lib/vip";

const EMOTION_COLORS = {
  joy: "from-amber-500/40 to-yellow-500/20",
  sadness: "from-blue-600/40 to-indigo-600/20",
  anxiety: "from-purple-500/40 to-violet-500/20",
  frustration: "from-red-500/40 to-orange-500/20",
  confusion: "from-slate-500/40 to-gray-500/20",
  curiosity: "from-cyan-500/40 to-teal-500/20",
  awe: "from-fuchsia-500/40 to-pink-500/20",
  determination: "from-emerald-500/40 to-green-500/20",
  skepticism: "from-zinc-500/40 to-stone-500/20",
  peace: "from-sky-400/40 to-blue-400/20",
  longing: "from-rose-500/40 to-pink-500/20",
  excitement: "from-orange-500/40 to-amber-500/20",
  neutral: "from-primary/30 to-primary/10",
};

export const OraclePopupPage = ({ language }) => {
  const [searchParams] = useSearchParams();
  const vipEmail = searchParams.get("email") || "";
  
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [currentEmotion, setCurrentEmotion] = useState("neutral");
  const [isPortalMode, setIsPortalMode] = useState(true);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim() || sending || !vipEmail) return;

    const userMsg = { role: "user", content: message.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setMessage("");

    try {
      setSending(true);

      const response = await sendVipChatMessage({
        email: vipEmail,
        message: userMsg.content,
        avatar_style: "oracle",
        detect_emotion: true,
      });

      const assistantMsg = {
        role: "assistant",
        content: response.reply,
        emotion: response.detected_emotion,
        emotionAck: response.emotional_acknowledgment,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      if (response.detected_emotion) {
        setCurrentEmotion(response.detected_emotion);
      } else {
        setCurrentEmotion("neutral");
      }

      // Voice reply
      if (voiceEnabled && response.reply) {
        try {
          const voiceResponse = await speakKonshusVoice({
            email: vipEmail,
            text: response.reply.substring(0, 500),
            generation_type: "popup-oracle",
          });
          if (voiceResponse.audio_url && audioRef.current) {
            audioRef.current.src = voiceResponse.audio_url;
            audioRef.current.play().catch(() => {});
          }
        } catch (voiceError) {
          console.error("Voice failed:", voiceError);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Oracle connection failed");
    } finally {
      setSending(false);
    }
  };

  const emotionGradient = EMOTION_COLORS[currentEmotion] || EMOTION_COLORS.neutral;

  if (!vipEmail) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a0e] text-slate-400">
        <p>No email provided. Close this window and try again.</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-[#0a0a0e]" data-testid="oracle-popup">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 bg-black/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">{KONSHUS_NAME}</span>
          {currentEmotion !== "neutral" && (
            <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] text-primary">
              sensing: {currentEmotion}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={() => setIsPortalMode(!isPortalMode)}
          >
            <Sparkles className="h-4 w-4" />
          </button>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={() => setVoiceEnabled(!voiceEnabled)}
          >
            {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Avatar Display */}
      <div className={`relative h-56 shrink-0 overflow-hidden bg-gradient-to-b ${emotionGradient}`}>
        {isPortalMode ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-10 animate-spin rounded-full border border-primary/20" style={{ animationDuration: "25s" }} />
              <div className="absolute -inset-16 animate-spin rounded-full border border-primary/10" style={{ animationDuration: "35s", animationDirection: "reverse" }} />
              <div className="absolute -inset-20 animate-spin rounded-full border border-dashed border-primary/5" style={{ animationDuration: "45s" }} />
              
              <div className="relative h-36 w-36 overflow-hidden rounded-full border-2 border-primary/50 shadow-[0_0_80px_rgba(212,175,55,0.4)]">
                <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-primary/40 via-transparent to-primary/30" />
                <video
                  autoPlay
                  className="h-full w-full object-cover opacity-90"
                  loop
                  muted
                  playsInline
                  poster={KONSHUS_POSTER_URL}
                  src={KONSHUS_VIDEO_URL}
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_40%,rgba(10,10,14,0.7)_100%)]" />
              </div>
              
              <div className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/60 blur-md" />
            </div>
          </div>
        ) : (
          <div className="relative h-full w-full">
            <video
              autoPlay
              className="h-full w-full object-cover"
              loop
              muted
              playsInline
              poster={KONSHUS_POSTER_URL}
              src={KONSHUS_VIDEO_URL}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0e] via-transparent to-transparent" />
          </div>
        )}
        
        {currentEmotion !== "neutral" && (
          <div className={`absolute inset-0 animate-pulse bg-gradient-to-t ${emotionGradient} opacity-40`} />
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-center text-sm text-slate-500">
              {pickText(language, "The oracle awaits your inquiry...", "L'oracolo attende la tua domanda...")}
            </p>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-primary/20 text-foreground"
                    : "border border-white/10 bg-black/40 text-slate-200"
                }`}
              >
                {msg.emotionAck && (
                  <p className="mb-2 text-xs italic text-primary/70">{msg.emotionAck}</p>
                )}
                <p className="text-sm leading-relaxed">{msg.content}</p>
                {msg.emotion && msg.emotion !== "neutral" && (
                  <p className="mt-1 text-[10px] text-slate-500">emotion detected: {msg.emotion}</p>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-white/10 bg-black/30 p-4">
        <div className="flex gap-2">
          <Input
            className="h-11 flex-1 border-white/10 bg-black/40 text-white placeholder:text-slate-500"
            disabled={sending}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder={pickText(language, "Speak to the oracle...", "Parla all'oracolo...")}
            value={message}
          />
          <Button
            className="h-11 w-11 rounded-full bg-primary p-0 text-black hover:bg-primary/80"
            disabled={sending || !message.trim()}
            onClick={handleSend}
          >
            {sending ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      <audio ref={audioRef} />
    </div>
  );
};
